import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { api } from "../../api/client";
import type { ContactData, InteractionData, TaskData } from "../../types";
import { Markdown } from "../Markdown";

interface Context {
  contacts: { id: string; data: ContactData; updated_at: string | null; interactions: { id: string; data: InteractionData }[]; tasks: { id: string; data: TaskData }[] }[];
  missing_contacts: string[];
  reply: { subject: string; sender: string | null; received_at: string | null; body_text: string | null; body_html: string | null } | null;
  reply_error: string | null;
}

// Render provider HTML as inert text, retaining paragraph breaks and never
// loading remote images, styles, or links from the original message.
function emailHtmlToText(html: string): string {
  const safe = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "div", "li"],
    ALLOWED_ATTR: [],
  }).replace(/<br\s*\/?\s*>|<\/(p|div|li)>/gi, "\n");
  return new DOMParser().parseFromString(safe, "text/html").body.textContent ?? "";
}

export function EmailReviewContext({ reviewId, version, isReply, replyTranslation }: {
  reviewId: string;
  version: number;
  isReply: boolean;
  /** The seat's translation of the message this draft answers (`reply_translation_md`). */
  replyTranslation?: string | null;
}) {
  const query = useQuery({
    queryKey: ["admin", "ai-team", "review-context", reviewId, version],
    queryFn: () => api.get<Context>(`/api/v1/admin/ai-team/reviews/${reviewId}/context`),
    staleTime: 60_000,
  });
  const context = query.data;
  return <div className="border-t border-rule mt-6 pt-5 space-y-5">
    <h2 className="font-heading font-semibold">Draft context</h2>
    <p className="text-xs text-ink-low">Live CRM overview for the current draft’s recipients, including CC/BCC. This is not a snapshot of everything the agent read when drafting.</p>
    {query.isPending && <p className="text-sm">Loading context…</p>}
    {query.isError && <div className="text-sm">Context could not be loaded. <button className="btn btn-sm btn-ghost" onClick={() => query.refetch()}>Retry</button></div>}
    {context && <>
      <section className="space-y-2">
        <h3 className="font-semibold">Original email</h3>
        {!isReply ? <p className="text-sm text-ink-low">New email — no reply message linked.</p> : context.reply ? <>
          <p className="text-sm font-semibold">{context.reply.subject}</p>
          <p className="text-xs text-ink-low">{context.reply.sender} · {context.reply.received_at ? new Date(context.reply.received_at).toLocaleString() : "Date unavailable"}</p>
          {context.reply.body_text ? <div className="text-sm whitespace-pre-wrap break-words max-h-96 overflow-y-auto">{context.reply.body_text}</div> : context.reply.body_html ? <div className="text-sm whitespace-pre-wrap break-words max-h-96 overflow-y-auto">{emailHtmlToText(context.reply.body_html)}</div> : <p className="text-sm text-ink-low">No message body available.</p>}
        </> : <p className="text-sm text-ink-low">{context.reply_error || "Original email unavailable."}</p>}
        {isReply && replyTranslation ? <div className="border border-rule rounded p-3 space-y-1">
          <p className="text-xs text-ink-low">Translation by the employee</p>
          <Markdown text={replyTranslation} />
        </div> : null}
      </section>
      <section className="space-y-4">
        <h3 className="font-semibold">CRM knowledge</h3>
        {context.missing_contacts.map((email) => <p key={email} className="text-sm text-ink-low">No CRM contact found for {email}.</p>)}
        {!context.contacts.length && !context.missing_contacts.length && <p className="text-sm text-ink-low">No recipients available for CRM lookup.</p>}
        {context.contacts.map(({ id, data, updated_at, interactions, tasks }) => <article key={id} className="border border-rule rounded p-3 space-y-2 text-sm">
          <Link className="font-semibold link" to={`/contacts/${id}`}>{data.personal.first_name} {data.personal.last_name}</Link>
          <p>{[data.personal.email, data.professional?.job_title, data.professional?.company_name || data.professional?.company_id].filter(Boolean).join(" · ")}</p>
          <p className="text-xs text-ink-low">CRM updated: {updated_at ? new Date(updated_at).toLocaleString() : "unknown"} · Last contact: {data.metadata?.last_contact_at || "not recorded"}</p>
          <p className="whitespace-pre-wrap">{data.metadata?.notes || "No contact notes recorded."}</p>
          {data.metadata?.tags?.length ? <p>Tags: {data.metadata.tags.join(", ")}</p> : null}
          {data.communication && <p>Communication: {Object.values(data.communication).filter(Boolean).join(" · ") || "not recorded"}</p>}
          {data.memberships?.pipelines?.map((p) => <p key={p.slug}>Pipeline: {p.slug} · {p.stage} · {p.status}</p>)}
          {data.memberships?.campaigns?.map((c) => <p key={c.slug}>Campaign: {c.slug} · {c.status}</p>)}
          <p className="font-semibold">Recent interactions (up to 5)</p>
          {!interactions.length && <p className="text-ink-low">None recorded.</p>}
          {interactions.map((i) => <div key={i.id}><p>{i.data.occurred_at?.slice(0, 10)} · {i.data.subject || i.data.interaction_type}</p><p className="text-ink-low whitespace-pre-wrap">{i.data.content?.summary || i.data.content?.body?.slice(0, 600)}</p></div>)}
          <p className="font-semibold">Open tasks (up to 5)</p>
          {!tasks.length && <p className="text-ink-low">None recorded.</p>}
          {tasks.map((t) => <p key={t.id}>{t.data.subject || t.data.task_type} · {t.data.status}{t.data.scheduled_date ? ` · ${t.data.scheduled_date}` : ""}</p>)}
        </article>)}
      </section>
    </>}
  </div>;
}
